import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Chip, Button, CircularProgress, Alert } from '@mui/material';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import type { Api, TenantView, DocReviewItem } from './api';

const ago = (s: string) => {
  const d = (Date.now() - new Date(s).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(s).toLocaleDateString();
};

// Docs review queue — every page whose latest revision is IN_REVIEW (incl. those
// submitted via the API/agent). Click to open it in the editor to publish or
// request changes.
export function DocReviews({ api, tenant, onOpen }: { api: Api; tenant: TenantView; onOpen: (pageId: string, spaceId: string) => void }) {
  const [items, setItems] = useState<DocReviewItem[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.docReviewQueue(tenant.tenantId).then(setItems).catch((e) => setErr(e.message)); }, [tenant.tenantId]);

  if (err) return <Alert severity="error">{err}</Alert>;
  if (!items) return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Documentation drafts waiting for review. Open one to edit &amp; publish, or request changes.
      </Typography>
      {items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <RateReviewOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
          <Typography sx={{ mt: 1 }}>Nothing in review. 🎉</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {items.map((it) => (
            <Paper key={it.pageId} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 600 }}>{it.title}</Typography>
                  <Chip size="small" label={`v ${it.version}`} variant="outlined" />
                  {it.source !== 'human' ? <Chip size="small" color="info" icon={<SmartToyOutlinedIcon />} label={it.source} /> : null}
                  {it.openComments > 0 ? <Chip size="small" color="warning" label={`${it.openComments} comment${it.openComments > 1 ? 's' : ''}`} /> : null}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>/{it.path}</Typography>
                {it.changeNote ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>“{it.changeNote}”</Typography> : null}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{ago(it.submittedAt)}</Typography>
              <Button variant="contained" size="small" onClick={() => onOpen(it.pageId, it.spaceId)}>Review</Button>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
